/* eslint-disable @blitz/block-scope-case */
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

export type MenuLink = {
  type: 'link' | 'bug' | 'feature';
  icon: React.ReactNode;
  title: string;
  url: string;
  delay?: number;
};

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'flex';

interface ButtonWithDropdownProps {
  links: MenuLink[];
  position?: Position;
  buttonClassName?: string;
  withBg?: boolean;
  framework?: string;
  openIcon?: React.ReactNode;
  closedIcon?: React.ReactNode;
}

const ButtonWithDropdown: React.FC<ButtonWithDropdownProps> = ({
  links,
  position = 'bottom-right',
  buttonClassName = 'my-2 w-full rounded-full bg-alpha-background-overlay-light text-white shadow-lg border border-alpha-background-overlay-light transition-colors duration-200',
  withBg = false,
  openIcon,
  closedIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [effectivePosition, setEffectivePosition] = useState<Exclude<Position, 'flex'>>(
    position === 'flex' ? 'top-left' : position,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (position === 'flex' && buttonRef.current && isOpen) {
      // Get button position and dimensions
      const rect = buttonRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Calculate available space in each direction
      const spaceAbove = rect.top;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceLeft = rect.left;
      const spaceRight = windowWidth - rect.right;

      // Determine best position - only for menu opening direction
      const verticalPosition = spaceBelow > spaceAbove ? 'top' : 'bottom';
      const horizontalPosition = spaceRight > spaceLeft ? 'left' : 'right';

      setEffectivePosition(`${verticalPosition}-${horizontalPosition}` as Exclude<Position, 'flex'>);
    } else if (position !== 'flex') {
      setEffectivePosition(position);
    }
  }, [isOpen, position]);

  const handleLinkClick = (link: MenuLink) => {
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Position-based styles - fixed positioning only for preset positions
  const menuContainerStyle =
    position === 'flex'
      ? { position: 'relative' as const } // Just make it relative for proper menu positioning
      : {
          position: 'fixed' as const,
          top: effectivePosition.includes('top') ? '1.5rem' : 'auto',
          bottom: effectivePosition.includes('bottom') ? '1.5rem' : 'auto',
          left: effectivePosition.includes('left') ? '1.5rem' : 'auto',
          right: effectivePosition.includes('right') ? '1.5rem' : 'auto',
        };

  // Animation values based on position
  const getAnimationValues = () => {
    switch (effectivePosition) {
      case 'top-left':
        return {
          initial: { opacity: 0, y: -20, x: -20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: -20, x: -20 },
          menuClassName: 'absolute top-full left-0 flex flex-col gap-2 mt-3',
          itemAnimation: { x: -20 },
        };
      case 'top-right':
        return {
          initial: { opacity: 0, y: -20, x: 20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: -20, x: 20 },
          menuClassName: 'absolute top-full right-0 flex flex-col gap-2 mt-3',
          itemAnimation: { x: 20 },
        };
      case 'bottom-left':
        return {
          initial: { opacity: 0, y: 20, x: -20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: 20, x: -20 },
          menuClassName: 'absolute bottom-full left-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: -20 },
        };
      case 'bottom-right':
        return {
          initial: { opacity: 0, y: 20, x: 20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: 20, x: 20 },
          menuClassName: 'absolute bottom-full right-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: 20 },
        };
      default:
        return {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 20 },
          menuClassName: 'absolute bottom-full right-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: 20 },
        };
    }
  };

  const animationValues = getAnimationValues();

  return (
    <div className="z-50 inline-block" style={menuContainerStyle}>
      {/* Dropdown Menu Items */}
      {isOpen && (
        <motion.div
          className={animationValues.menuClassName}
          initial={animationValues.initial}
          animate={animationValues.animate}
          exit={animationValues.exit}
          style={
            withBg
              ? {
                  backgroundColor: 'rgba(20, 20, 20, 1)',
                  padding: '12px',
                  borderRadius: '12px',
                }
              : {}
          }
        >
          {links.map((link, index) => (
            <motion.div
              onClick={() => handleLinkClick(link)}
              key={index}
              className="cursor-pointer flex items-center gap-2 bg-zinc-800/90 hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border border-zinc-700/50 shadow-md whitespace-nowrap"
              initial={{ opacity: 0, ...animationValues.itemAnimation }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: link.delay || index * 0.1 }}
            >
              {link.icon}
              {link.title}
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Main Button */}
      <motion.button
        ref={buttonRef}
        onClick={toggleMenu}
        className={buttonClassName}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={isOpen ? { rotate: 0 } : { rotate: 0 }}
      >
        {isOpen ? openIcon : closedIcon}
      </motion.button>
    </div>
  );
};

export default ButtonWithDropdown;
